import { ComponentProps, ReactNode } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { Combobox, ComboboxInput } from '@/components/core/combobox';

import { DefaultFormField, FormFieldProps } from './default-form-field';

type ComboboxFormFieldControlProps<TItem> = Omit<
  ComponentProps<typeof Combobox>,
  'className' | 'defaultValue' | 'disabled' | 'name' | 'onValueChange' | 'value'
> &
  Pick<ComponentProps<typeof ComboboxInput>, 'aria-label' | 'className' | 'placeholder'> & {
    children: ReactNode;
    items: TItem[];
  };

export function ComboboxFormField<
  TItem,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  description,
  disabled,
  hideError,
  label,
  name,
  children,
  items,
  className,
  placeholder,
  'aria-label': ariaLabel,
  multiple,
  ...props
}: FormFieldProps<ComboboxFormFieldControlProps<TItem>, TFieldValues, TName>) {
  return (
    <DefaultFormField
      control={control}
      description={description}
      disabled={disabled}
      hideError={hideError}
      label={label}
      name={name}
      render={({ field }) => {
        let value: string | string[] | null;

        if (multiple) {
          value = Array.isArray(field.value) ? field.value : [];
        } else {
          value = typeof field.value === 'string' ? field.value : null;
        }

        return (
          <Combobox
            {...props}
            disabled={field.disabled}
            items={items}
            multiple={multiple}
            name={field.name}
            value={value}
            onValueChange={(nextValue) => {
              if (multiple) {
                field.onChange(Array.isArray(nextValue) ? nextValue : []);
                return;
              }

              field.onChange(typeof nextValue === 'string' ? nextValue : null);
            }}
          >
            <ComboboxInput
              aria-label={ariaLabel}
              className={className}
              disabled={field.disabled}
              onBlur={field.onBlur}
              placeholder={placeholder}
            />
            {children}
          </Combobox>
        );
      }}
    />
  );
}
